import React, { useState, useEffect } from 'react';
import { MessageSquare, Clock, Loader2, CornerUpLeft, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/Pagination';
import { useI18n } from '@/contexts/I18nContext';
import { formatDate as formatDateI18n } from '@/i18n';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { apiManager } from '../../services/implementations/APIManager';
import { router, AppRoute } from '../../lib/router';
import type { ChatSessionWithPreviewTurns } from '../../types/backend';
import { toast } from 'sonner';

interface SessionListPageProps {
  onSessionSelect: (session: ChatSessionWithPreviewTurns) => void;
}

export const SessionListPage: React.FC<SessionListPageProps> = ({ onSessionSelect }) => {
  const { t, currentLanguage } = useI18n();

  const [chats, setChats] = useState<ChatSessionWithPreviewTurns[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  
  // 🔑 AlertDialog 状态管理
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    loadChats(currentPage);
  }, [currentPage]);

  const loadChats = async (page: number) => {
    try {
      setLoading(true);
      const response = await apiManager.getChatsOptimized(page, 10);
      
      setChats(response.sessions || []);
      setTotalPages(response.pagination.total_pages);
      setTotal(response.pagination.total);
      
      console.log('📄 会话列表加载完成:', {
        page,
        sessions: response.sessions.length,
        totalPages: response.pagination.total_pages,
        total: response.pagination.total
      });
    } catch (err) {
      console.error('Failed to load chats:', err);
      toast.error(t('chat.loadingConversations'));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 🔑 打开删除确认弹窗
  const handleDeleteClick = (sessionId: string, sessionTitle: string, event: React.MouseEvent) => {
    // 阻止事件冒泡，避免触发会话选择
    event.stopPropagation();
    
    setSessionToDelete({ id: sessionId, title: sessionTitle });
    setDeleteDialogOpen(true);
  };

  // 🔑 确认删除会话
  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;

    const { id: sessionId, title: sessionTitle } = sessionToDelete;

    try {
      setDeletingIds(prev => new Set(prev).add(sessionId));
      
      console.log('🗑️ 开始删除会话:', { sessionId, sessionTitle });
      
      const response = await apiManager.deleteChatSession(sessionId);
      
      console.log('✅ 删除会话成功:', response);
      
      // 🔑 从本地状态中移除该会话，避免重新调用接口
      setChats(prevChats => prevChats.filter(chat => chat.id !== sessionId));
      
      // 更新总数
      setTotal(prev => prev - 1);
      
      // 显示成功提示
      const deletedTurnsCount = response.deleted_turns_count || 0;
      toast.success(`✅ ${response.message || `已删除会话 "${sessionTitle}"`}${deletedTurnsCount > 0 ? ` (${deletedTurnsCount}条对话记录)` : ''}`);
      
      // 🔑 如果当前页没有数据了，且不是第一页，则回退到上一页
      if (chats.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      }
      
    } catch (err) {
      console.error('❌ 删除会话失败:', err);
      const errorMessage = err instanceof Error ? err.message : '删除失败';
      toast.error(`❌ 删除失败: ${errorMessage}`);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
      // 关闭弹窗
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    }
  };

  // 🔑 取消删除
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  const formatDate = (dateString: string) => {
    return formatDateI18n(dateString, currentLanguage);
  };

  const handleBackToHome = () => {
    router.navigate(AppRoute.HOME);
  };

  return (
    <div className="container mx-auto px-4 py-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={handleBackToHome}
            variant="ghost"
            size="sm"
            className="gap-2"
          >
            <CornerUpLeft className="h-4 w-4" />
            {/* 返回主页 */}
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">{t('chat.conversationHistory')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('chat.browseHistory')} ({t('chat.totalSessions').replace('{count}', total.toString())})
            </p>
          </div>
        </div>
        <Button 
          onClick={() => loadChats(currentPage)} 
          variant="outline" 
          size="sm" 
          disabled={loading}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {t('common.refresh')}
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
          </div>
        ) : chats.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t('chat.noConversations')}</p>
            <p className="text-sm mt-1">{t('chat.startNewConversation')}</p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 mb-6">
              {chats.map((chat) => (
                <Card
                  key={chat.id}
                  className="cursor-pointer hover:shadow-md transition-shadow relative group"
                  onClick={() => onSessionSelect(chat)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg pr-2">{chat.title}</CardTitle>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="secondary" className="text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatDate(chat.created_at)}
                        </Badge>
                        {/* 🔑 删除按钮 */}
                        <Button
                          onClick={(e) => handleDeleteClick(chat.id, chat.title, e)}
                          variant="ghost"
                          size="sm"
                          className="opacity-60 group-hover:opacity-100 transition-opacity h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-100 border border-transparent hover:border-red-200"
                          disabled={deletingIds.has(chat.id)}
                          title={`${t('chat.deleteSession')} "${chat.title}"`}
                        >
                          {deletingIds.has(chat.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {chat.last_message || t('chat.noContent')}
                    </p>
                    <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                      <MessageSquare className="h-3 w-3" />
                      <span>{t('chat.turnsCount').replace('{count}', chat.total_turns_count.toString())}</span>
                      {chat.conversation_turns.length > 0 && (
                        <span>• {t('chat.preview').replace('{count}', chat.conversation_turns.length.toString())}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* 分页器 */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </>
        )}
      </div>

      {/* 🔑 删除确认弹窗 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('chat.confirmDeleteSession')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('chat.deleteSessionWarning').replace('{title}', sessionToDelete?.title || '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {t('chat.confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}; 